import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'glass' | 'white' | 'glassDark' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  isLoading?: boolean;
}

const Button: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  isLoading, 
  className = '', 
  disabled,
  ...props 
}) => {
  const baseStyle = "inline-flex items-center justify-center rounded-xl font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]";
  
  const variants = {
    primary: "bg-stone-900 text-white shadow-xl shadow-stone-900/20 hover:bg-black hover:shadow-2xl hover:shadow-stone-900/30 hover:-translate-y-0.5 focus:ring-stone-900 border border-transparent",
    secondary: "bg-stone-100 text-stone-900 border border-stone-200 hover:bg-stone-200 hover:border-stone-300 focus:ring-stone-300 shadow-sm",
    outline: "border border-stone-200 text-stone-600 bg-transparent hover:bg-stone-50 hover:text-stone-900 hover:border-stone-300 focus:ring-stone-200",
    ghost: "text-stone-500 hover:text-stone-900 hover:bg-stone-100/50",
    glass: "bg-white/70 backdrop-blur-md border border-white/40 text-stone-800 shadow-sm hover:bg-white hover:shadow-lg hover:shadow-stone-200/50",
    white: "bg-white text-stone-900 shadow-lg shadow-stone-200/50 hover:bg-stone-50 hover:shadow-xl hover:-translate-y-0.5 border border-stone-100",
    glassDark: "bg-black/20 text-white border border-white/10 hover:bg-black/30 backdrop-blur-md focus:ring-white/20 shadow-inner",
    danger: "bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 hover:border-red-200",
  };

  const sizes = {
    sm: "px-3 py-1.5 text-xs tracking-wide font-semibold",
    md: "px-5 py-2.5 text-sm",
    lg: "px-8 py-3.5 text-base font-semibold tracking-tight",
    icon: "p-2 aspect-square"
  };

  return (
    <button 
      className={`${baseStyle} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={isLoading || disabled}
      {...props}
    >
      {isLoading ? (
        <span className="flex items-center gap-2">
          <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="opacity-90">Processing</span>
        </span>
      ) : children}
    </button>
  );
};

export default Button;